import { Desktop, DeviceMobile, SignOut } from '@phosphor-icons/react';
import type { SessionDto } from '@reelplicate/api-client/model';
import {
  getListMySessionsQueryKey,
  useListMySessions,
  useRevokeMyOtherSessions,
  useRevokeMySession,
} from '@reelplicate/api-client/users';
import { Badge, Button, toast } from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';

import { sessionDevice, sessionMeta } from './session-labels';
import { ListRow, SettingsCard, SettingsSection } from './settings-layout';

/**
 * "Where you're signed in" on Settings → Account & security: the user's
 * Clerk sessions from the API, with sign-out for each other one and for
 * all of them at once.
 */
export function SignedInSessions() {
  const queryClient = useQueryClient();
  const { data: sessions } = useListMySessions();
  const revokeOne = useRevokeMySession();
  const revokeOthers = useRevokeMyOtherSessions();

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: getListMySessionsQueryKey() });

  // API failures already raise a toast; only success is announced here.
  const signOut = async (session: SessionDto) => {
    try {
      await revokeOne.mutateAsync({ sessionId: session.id });
    } catch {
      return;
    }
    await refresh();
    toast.success(`Signed out of ${sessionDevice(session)}`);
  };

  const signOutOthers = async () => {
    try {
      await revokeOthers.mutateAsync();
    } catch {
      return;
    }
    await refresh();
    toast.success('Signed out everywhere else');
  };

  const others = sessions?.some((session) => !session.current) ?? false;

  return (
    <SettingsSection
      title="Where you’re signed in"
      action={
        <Button
          type="button"
          variant="ghost"
          size="sm"
          leadingIcon={SignOut}
          disabled={!others}
          loading={revokeOthers.isPending}
          onClick={() => void signOutOthers()}
        >
          Sign out everywhere else
        </Button>
      }
    >
      <SettingsCard>
        {sessions?.map((session) => (
          <ListRow
            key={session.id}
            tile={
              session.isMobile ? (
                <DeviceMobile aria-hidden className="text-icon-primary size-5" />
              ) : (
                <Desktop aria-hidden className="text-icon-primary size-5" />
              )
            }
            title={sessionDevice(session)}
            titleBadge={session.current && <Badge tone="brand">This device</Badge>}
            meta={sessionMeta(session)}
          >
            {!session.current && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                aria-label={`Sign out of ${sessionDevice(session)}`}
                loading={
                  revokeOne.isPending && revokeOne.variables?.sessionId === session.id
                }
                onClick={() => void signOut(session)}
              >
                Sign out
              </Button>
            )}
          </ListRow>
        ))}
      </SettingsCard>
    </SettingsSection>
  );
}
